/**
 * Restricted n-body craft integrator (RK4) and force model.
 *
 * Accelerations (default **nbody**): Earth + Moon + Sun point-mass gravity,
 * Earth J₂, and a US76-ish piecewise atmosphere + quadratic drag below ~150 km.
 *
 * **earth** model: Earth + Sun point-mass + J₂ + atmosphere (no Moon). The Sun
 * stays in both models: the frame is heliocentric, so dropping it lets Earth
 * fall sunward and pumps low-Earth-orbit eccentricity. The Moon is the
 * third-body term the earth model is for checking.
 *
 * Units: km, s, km/s, km/s².
 */

/** Which gravitational terms to include (atmosphere/drag always on when vel given). */
export type GravityModel = "nbody" | "earth";

export type AccelOptions = {
  /**
   * Force model. Default `"nbody"`.
   * `"earth"` = Earth μ + Sun μ + J₂ + drag (no Moon).
   */
  gravity?: GravityModel;
  /**
   * Body ephemeris for this integrate step.
   * Default {@link DEFAULT_EPHEMERIS} (analytic, no Horizons).
   */
  epoch?: EphemerisEpoch;
  /**
   * Quadratic-drag ballistic factor Cd·A/m (km²/kg).
   * Default {@link DRAG_CD_A_OVER_M} (ascent stack). Detached Super Heavy is
   * lighter, so recovery passes a larger value.
   */
  dragCdAOverM?: number;
};

import {
  DRAG_CD_A_OVER_M,
  DT_COAST,
  DT_NEAR,
  EARTH_J2,
  EARTH_SIDEREAL_DAY_S,
  MU_EARTH,
  MU_MOON,
  MU_SUN,
  R_MOON,
} from "./constants";
import { atmDensity } from "./atmosphere";
import { bodyPositions, type BodyState } from "./bodies";
import {
  DEFAULT_EPHEMERIS,
  type EphemerisEpoch,
} from "./ephemerisEpoch";
import { earthNorthPole } from "./earthFrame";
import { radialHeightAboveEllipsoid, WGS84_A } from "./wgs84";
import {
  add,
  clone,
  copy,
  cross,
  dot,
  len,
  madd,
  set,
  sub,
  type V3,
  v3,
} from "./vec3";

function resolveEpoch(opts?: AccelOptions): EphemerisEpoch {
  return opts?.epoch ?? DEFAULT_EPHEMERIS;
}

/** Craft state in the heliocentric theater frame. */
export type CraftState = {
  /** Mission time (s). */
  t: number;
  /** Inertial position (km). */
  pos: V3;
  /** Inertial velocity (km/s). */
  vel: V3;
};

const _bodies: BodyState = {
  sun: v3(),
  earth: v3(),
  moon: v3(),
  earthVel: v3(),
  moonVel: v3(),
};

const _r = v3();
const _a = v3();
const _tmp = v3();
const _pole = v3();
const _vRel = v3();
const _omega = v3();
const _vAtm = v3();

/** Earth spin rate (rad/s) for co-rotating atmosphere. */
const EARTH_OMEGA = (2 * Math.PI) / EARTH_SIDEREAL_DAY_S;

/** Point-mass gravity from one body: −μ r̂ / r² */
function addGravity(acc: V3, craft: V3, body: V3, mu: number): void {
  sub(_r, craft, body);
  const r = len(_r);
  if (r < 1e-6) return;
  const f = -mu / (r * r * r);
  acc.x += _r.x * f;
  acc.y += _r.y * f;
  acc.z += _r.z * f;
}

/**
 * Earth J₂ acceleration in the inertial frame.
 * a = 1½ J₂ μ R² / r⁵ · [ (5 ζ² − 1) r − 2 ζ n̂ ]
 * where ζ = (r · n̂)/r and n̂ is the Earth north pole.
 */
function j2Accel(acc: V3, r: number, zeta: number): void {
  const r2 = r * r, fac = 1.5 * EARTH_J2 * MU_EARTH * (WGS84_A * WGS84_A) / (r2 * r2 * r);
  const s = (5 * (zeta * zeta) / r2 - 1);
  acc.x += fac * (s * _r.x - 2 * zeta * _pole.x);
  acc.y += fac * (s * _r.y - 2 * zeta * _pole.y);
  acc.z += fac * (s * _r.z - 2 * zeta * _pole.z);
}

export function addEarthJ2(acc: V3, craft: V3, earth: V3): void {
  sub(_r, craft, earth);
  const r = len(_r);
  if (r < WGS84_A * 0.5) return;
  earthNorthPole(_pole);
  j2Accel(acc, r, dot(_r, _pole));
}

export { atmDensity };

/**
 * Quadratic drag vs co-rotating atmosphere.
 * a = −½ (Cd A/m) ρ |v_rel| v_rel
 * Uses fixed ballistic factor (theater stack).
 */
function dragRelVel(vel: V3, earthVel: V3): number {
  earthNorthPole(_pole);
  set(_omega, _pole.x * EARTH_OMEGA, _pole.y * EARTH_OMEGA, _pole.z * EARTH_OMEGA);
  cross(_vAtm, _omega, _r);
  _vRel.x = vel.x - earthVel.x - _vAtm.x;
  _vRel.y = vel.y - earthVel.y - _vAtm.y;
  _vRel.z = vel.z - earthVel.z - _vAtm.z;
  return len(_vRel);
}

function applyDrag(acc: V3, rho: number, speed: number, cdAOverM: number): void {
  const k = -0.5 * cdAOverM * rho * speed;
  acc.x += k * _vRel.x; acc.y += k * _vRel.y; acc.z += k * _vRel.z;
}

export function addEarthDrag(
  acc: V3, craft: V3, earth: V3, vel: V3, earthVel: V3,
  cdAOverM: number = DRAG_CD_A_OVER_M,
): void {
  sub(_r, craft, earth);
  earthNorthPole(_pole);
  const rho = atmDensity(radialHeightAboveEllipsoid(_r, _pole));
  if (rho < 1e-30) return;
  const speed = dragRelVel(vel, earthVel);
  if (speed >= 1e-9) applyDrag(acc, rho, speed, cdAOverM);
}

/**
 * Gravitational acceleration on craft at time t (optional thrust + Earth J2/drag).
 *
 * Default restricted n-body: Earth, Moon, and Sun point masses, plus J₂ /
 * drag / thrust. The Sun is a full point mass because Earth is on a
 * heliocentric ephemeris; the solar tide is what remains after subtracting
 * that ephemeris acceleration. `{ gravity: "earth" }` drops only the Moon.
 * Pass `vel` to include atmospheric drag; omit for pure gravity+J2.
 */
function addNbodyTerms(out: V3, pos: V3): void {
  addGravity(out, pos, _bodies.moon, MU_MOON);
}

function addThrust(out: V3, thrust: V3 | null): void {
  if (!thrust) return;
  out.x += thrust.x; out.y += thrust.y; out.z += thrust.z;
}

function earthBaseAccel(out: V3, pos: V3): void {
  addGravity(out, pos, _bodies.earth, MU_EARTH);
  addEarthJ2(out, pos, _bodies.earth);
  // Heliocentric frame: Earth accelerates toward the Sun. Omitting this
  // common mode shows up as a once-per-rev eccentricity pump in LEO.
  addGravity(out, pos, _bodies.sun, MU_SUN);
}

export function acceleration(
  t: number, pos: V3, thrust: V3 | null, out: V3 = _a, vel: V3 | null = null, opts?: AccelOptions,
): V3 {
  const gravity: GravityModel = opts?.gravity ?? "nbody";
  bodyPositions(t, resolveEpoch(opts), _bodies);
  set(out, 0, 0, 0);
  earthBaseAccel(out, pos);
  if (gravity === "nbody") addNbodyTerms(out, pos);
  if (vel) {
    addEarthDrag(
      out, pos, _bodies.earth, vel, _bodies.earthVel,
      opts?.dragCdAOverM ?? DRAG_CD_A_OVER_M,
    );
  }
  addThrust(out, thrust);
  return out;
}

const k1r = v3(),
  k1v = v3(),
  k2r = v3(),
  k2v = v3(),
  k3r = v3(),
  k3v = v3(),
  k4r = v3(),
  k4v = v3(),
  rp = v3(),
  vp = v3(),
  thr = v3();

export type ThrustFn = (t: number, pos: V3, vel: V3) => V3 | null;

/**
 * Classic RK4 step. Mutates state in place.
 * thrustFn returns inertial acceleration (km/s²) or null.
 * Includes Earth J2 + atmospheric drag (when in atmosphere).
 * Pass `{ gravity: "earth" }` to drop Moon / solar-tide terms.
 */
function rk4Stage(
  t: number, pos: V3, vel: V3, kr: V3, kv: V3, h: number,
  thrustFn: ThrustFn | undefined, opts: AccelOptions | undefined,
  outR: V3, outV: V3,
): void {
  madd(rp, pos, kr, h);
  madd(vp, vel, kv, h);
  const th = thrustFn?.(t + h, rp, vp) ?? null;
  acceleration(t + h, rp, th, outV, vp, opts);
  copy(outR, vp);
}

function rk4Combine(dst: V3, k1: V3, k2: V3, k3: V3, k4: V3, dt: number): void {
  dst.x += (dt / 6) * (k1.x + 2 * k2.x + 2 * k3.x + k4.x);
  dst.y += (dt / 6) * (k1.y + 2 * k2.y + 2 * k3.y + k4.y);
  dst.z += (dt / 6) * (k1.z + 2 * k2.z + 2 * k3.z + k4.z);
}

function rk4First(state: CraftState, thrustFn: ThrustFn | undefined, opts: AccelOptions | undefined): void {
  const th0 = thrustFn?.(state.t, state.pos, state.vel) ?? null;
  if (th0) copy(thr, th0);
  acceleration(state.t, state.pos, th0, k1v, state.vel, opts);
  copy(k1r, state.vel);
}

function rk4Stages(state: CraftState, dt: number, thrustFn: ThrustFn | undefined, opts: AccelOptions | undefined): void {
  const { t, pos, vel } = state;
  rk4Stage(t, pos, vel, k1r, k1v, dt * 0.5, thrustFn, opts, k2r, k2v);
  rk4Stage(t, pos, vel, k2r, k2v, dt * 0.5, thrustFn, opts, k3r, k3v);
  rk4Stage(t, pos, vel, k3r, k3v, dt, thrustFn, opts, k4r, k4v);
}

export function rk4Step(state: CraftState, dt: number, thrustFn?: ThrustFn, opts?: AccelOptions): void {
  rk4First(state, thrustFn, opts);
  rk4Stages(state, dt, thrustFn, opts);
  rk4Combine(state.pos, k1r, k2r, k3r, k4r, dt);
  rk4Combine(state.vel, k1v, k2v, k3v, k4v, dt);
  state.t += dt;
}

/**
 * Coast RK4 step (s) from distance to the nearby dominant body (Moon on
 * translunar coast, Earth on suborbital entry). Finer than {@link DT_NEAR}
 * inside ~8_000 km so perilune / low-alt curvature is not under-sampled.
 */
export function nearBodyCoastDt(distanceKm: number): number {
  if (!(distanceKm > 0) || !Number.isFinite(distanceKm)) return DT_NEAR;
  if (distanceKm < 8_000) return 0.5;
  if (distanceKm < 40_000) return 1;
  if (distanceKm < 120_000) return 5;
  if (distanceKm < 250_000) return 12;
  return DT_COAST;
}

/** Copy a craft state (new position/velocity objects). */
export function cloneCraftState(state: CraftState): CraftState {
  return { t: state.t, pos: clone(state.pos), vel: clone(state.vel) };
}

/**
 * Moon-relative two-body specific energy (km²/s²). Not conserved under Earth
 * perturbation; used as a Jacobi-ish integrator consistency check.
 */
export function moonRelativeSpecificEnergy(
  t: number,
  pos: V3,
  vel: V3,
  epoch: EphemerisEpoch = DEFAULT_EPHEMERIS,
): number {
  const b = getBodies(t, epoch);
  sub(_r, pos, b.moon);
  const r = len(_r);
  if (r < 1e-6) return Number.NEGATIVE_INFINITY;
  const vx = vel.x - b.moonVel.x;
  const vy = vel.y - b.moonVel.y;
  const vz = vel.z - b.moonVel.z;
  return 0.5 * (vx * vx + vy * vy + vz * vz) - MU_MOON / r;
}

/** Radius (km) inside which near-Moon step-doubling diagnostics run. */
export const NEAR_MOON_QUALITY_KM = 250_000;

/** Peak RK4 step-doubling disagreement near the Moon. */
export type NearMoonQuality = {
  maxStepErrKm: number;
  maxEnergyRelResidual: number;
  samples: number;
};

/** Empty near-Moon integrator diagnostics. */
export function createNearMoonQuality(): NearMoonQuality {
  return { maxStepErrKm: 0, maxEnergyRelResidual: 0, samples: 0 };
}

/**
 * Position (km) and Moon-relative energy disagreement between one RK4 step of
 * `dt` and two of `dt/2`. Does not mutate `state`.
 */
export function rk4StepDoubling(
  state: CraftState,
  dt: number,
  opts?: AccelOptions,
): { posErrKm: number; energyRelResidual: number } {
  const a = cloneCraftState(state);
  const b = cloneCraftState(state);
  rk4Step(a, dt, undefined, opts);
  rk4Step(b, dt * 0.5, undefined, opts);
  rk4Step(b, dt * 0.5, undefined, opts);
  const posErrKm = Math.hypot(a.pos.x - b.pos.x, a.pos.y - b.pos.y, a.pos.z - b.pos.z);
  const epoch = opts?.epoch ?? DEFAULT_EPHEMERIS;
  const eA = moonRelativeSpecificEnergy(a.t, a.pos, a.vel, epoch);
  const eB = moonRelativeSpecificEnergy(b.t, b.pos, b.vel, epoch);
  const denom = Math.max(Math.abs(eA), Math.abs(eB), 1e-12);
  const energyRelResidual = Math.abs(eA - eB) / denom;
  return { posErrKm, energyRelResidual };
}

const QUALITY_EVERY = 8;

/**
 * Sample step-doubling error near the Moon. Skip burns (non-conservative).
 * Call on the pre-step state with the dt about to be taken.
 */
export function noteNearMoonQuality(
  q: NearMoonQuality,
  state: CraftState,
  dt: number,
  dMoon: number,
  epoch: EphemerisEpoch,
): void {
  if (!(dMoon < NEAR_MOON_QUALITY_KM) || !(dt > 0)) return;
  q.samples += 1;
  if (q.samples !== 1 && q.samples % QUALITY_EVERY !== 0) return;
  const { posErrKm, energyRelResidual } = rk4StepDoubling(state, dt, { epoch });
  if (posErrKm > q.maxStepErrKm) q.maxStepErrKm = posErrKm;
  if (energyRelResidual > q.maxEnergyRelResidual) q.maxEnergyRelResidual = energyRelResidual;
}

/** Surface collision / proximity checks. */
export function altitudeEarth(
  t: number,
  pos: V3,
  epoch: EphemerisEpoch = DEFAULT_EPHEMERIS,
): number {
  bodyPositions(t, epoch, _bodies);
  sub(_tmp, pos, _bodies.earth);
  earthNorthPole(_pole);
  return radialHeightAboveEllipsoid(_tmp, _pole);
}

export function altitudeMoon(
  t: number,
  pos: V3,
  epoch: EphemerisEpoch = DEFAULT_EPHEMERIS,
): number {
  bodyPositions(t, epoch, _bodies);
  return len(sub(_tmp, pos, _bodies.moon)) - R_MOON;
}

export function distanceToMoon(
  t: number,
  pos: V3,
  epoch: EphemerisEpoch = DEFAULT_EPHEMERIS,
): number {
  bodyPositions(t, epoch, _bodies);
  return len(sub(_tmp, pos, _bodies.moon));
}

export function distanceToEarth(
  t: number,
  pos: V3,
  epoch: EphemerisEpoch = DEFAULT_EPHEMERIS,
): number {
  bodyPositions(t, epoch, _bodies);
  return len(sub(_tmp, pos, _bodies.earth));
}

export function getBodies(
  t: number,
  epoch: EphemerisEpoch = DEFAULT_EPHEMERIS,
): BodyState {
  return bodyPositions(t, epoch, _bodies);
}

/** Impulsive Δv in inertial frame. */
export function applyDeltaV(vel: V3, dv: V3): void {
  add(vel, vel, dv);
}
